import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Shield, Plus, Edit3, Trash2, Eye, Check, Search, Copy, ChevronDown, PanelLeft, X, History, Power, PowerOff, Wrench, MoreHorizontal, Rows, Download, Upload, ArrowLeftRight, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { NAV_GROUPS, HEADER_ITEMS } from "@/components/Layout/AppShell";
import AdvancedTable from "@/components/table/AdvancedTable";

const LANDING_OPTIONS = NAV_GROUPS.flatMap((g) => g.items.map((n) => ({ value: n.to, label: n.label })));

export default function Roles() {
  const { activeWorkspaceId, memberships, user: me } = useAuth();
  const activeMembership = memberships?.find((m) => m.workspace_id === activeWorkspaceId);
  const isAdmin = ["owner", "admin", "workspace_owner", "workspace_admin"].includes(activeMembership?.role);
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [modules, setModules] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null); // role doc or {} for new
  const [inspecting, setInspecting] = useState(null);
  const [auditFor, setAuditFor] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const [r, c] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/roles`),
        api.get(`/permissions`),
      ]);
      setRows(r.data || []);
      setGroups(c.data?.groups || []);
      setModules(c.data?.modules || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  const remove = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/workspaces/${activeWorkspaceId}/roles/${role.id}`); toast.success("Role deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  // R158x · Feb 2026 — Deactivate flow. Always opens the shadcn dialog
  // (even when the role has 0 holders) for a consistent look; the dialog
  // adapts its copy to "No holders — confirm to deactivate directly" in
  // the empty case. Legacy window.confirm removed for UX parity.
  const [deactivating, setDeactivating] = useState(null);
  // R164a · Feb 2026 — Cleanup orphan role assignments modal state.
  const [showCleanup, setShowCleanup] = useState(false);
  // Feb 2026 · Role templates picker (Studio Manager · Studio Viewer · …)
  const [tplPickerOpen, setTplPickerOpen] = useState(false);
  // Feb 2026 · Super-admin orphan-role sweep across all workspaces
  const [sweepOpen, setSweepOpen] = useState(false);
  // Feb 2026 · Bulk compare & edit mode.
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState(() => new Set());
  const [compareOpen, setCompareOpen] = useState(false);
  const toggleCompareId = (id) => setCompareIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else if (n.size >= 10) { toast.warning("Compare up to 10 roles at a time"); return n; }
    else n.add(id);
    return n;
  });
  const handleDeactivate = (role) => setDeactivating(role);
  const activateRole = (role) => {
    api.post(`/workspaces/${activeWorkspaceId}/roles/${role.id}/activate`)
      .then(() => { toast.success(`Role "${role.name}" reactivated`); load(); })
      .catch((e) => toast.error(apiErrorMessage(e, "Activate failed")));
  };

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => r.name.toLowerCase().includes(q) || r.slug.toLowerCase().includes(q));
  }, [rows, search]);

  const roleColumns = useMemo(() => [
    {
      key: "name", label: "Role", width: 220, sortable: true, filterType: "text",
      accessor: (r) => `${r.name} ${r.slug}`,
      render: (r) => (
        <div className="min-w-0">
          <div className="font-semibold truncate">{r.name}</div>
          <div className="text-[10px] text-foreground/50 font-mono truncate">{r.slug}</div>
        </div>
      ),
      exportValue: (r) => `${r.name} <${r.slug}>`,
    },
    {
      key: "description", label: "Description", width: 320, sortable: true, filterType: "text",
      accessor: (r) => r.description || "",
      render: (r) => <span className="text-xs text-foreground/70 truncate block">{r.description || "—"}</span>,
    },
    {
      key: "permissions_count", label: "Permissions", width: 140, sortable: true, align: "right",
      filterType: "number-range",
      accessor: (r) => (r.permissions || []).length,
      render: (r) => (
        <div className="flex items-center gap-1 justify-end">
          <span className="chip text-[10px]">{(r.permissions || []).length} perms</span>
          {r.is_system && <span className="chip text-[10px] chip-internal">system</span>}
        </div>
      ),
      exportValue: (r) => (r.permissions || []).length,
    },
    {
      key: "assignee_count", label: "Assignees", width: 100, sortable: true, align: "right", sum: true,
      filterType: "number-range",
      accessor: (r) => r.assignee_count || 0,
      render: (r) => <span className="chip text-[10px]">{r.assignee_count || 0}</span>,
    },
  ], []);

  return (
    <>
      <PageHeader
        eyebrow="HR & Org"
        title="Workspace roles"
        description="Configurable role catalog for this workspace. Edit permissions, delete unused roles, or create new ones tailored to your team."
        testid="roles-header"
        actions={isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button className="rounded-none" onClick={() => setEditing({ name: "", description: "", permissions: [] })} data-testid="roles-new-btn">
              <Plus className="w-4 h-4 mr-2" /> New role
            </Button>
            <Button variant="outline" className="rounded-none" onClick={() => setTplPickerOpen(true)}
                    data-testid="roles-from-template-btn"
                    title="Add a preset role — Studio Manager, Studio Viewer, etc.">
              <Shield className="w-4 h-4 mr-2" /> From template
            </Button>
            <Button
              variant={compareMode ? "default" : "outline"}
              className="rounded-none"
              onClick={() => { setCompareMode((v) => !v); setCompareIds(new Set()); }}
              data-testid="roles-compare-toggle"
              title="Enter compare mode — select 2 to 10 roles then click Compare"
            >
              <ArrowLeftRight className="w-4 h-4 mr-2" />
              {compareMode ? `Comparing (${compareIds.size})` : "Compare"}
            </Button>
            {/* Feb 2026 · Secondary actions consolidated into a single kebab
                so the header stops overflowing the title band on desktop. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-none" data-testid="roles-more-btn"
                          title="Import / Export / Duplicate / Cleanup / Sync presets">
                  <MoreHorizontal className="w-4 h-4 mr-2" /> More
                  <ChevronDown className="w-3.5 h-3.5 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none w-64">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-foreground/50">
                  Bulk actions
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="rounded-none cursor-pointer"
                                    onClick={async () => {
                                      try {
                                        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/roles/export`);
                                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                                        const link = document.createElement("a");
                                        link.href = URL.createObjectURL(blob);
                                        link.download = `acquilix-roles-${(data.exported_from_workspace || "workspace").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${new Date().toISOString().split("T")[0]}.json`;
                                        link.click();
                                        toast.success(`Exported ${data.count} role(s)`);
                                      } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
                                    }} data-testid="roles-export-btn">
                  <Download className="w-4 h-4 mr-2" /> Export all as JSON
                </DropdownMenuItem>
                <DropdownMenuItem className="rounded-none cursor-pointer"
                                    onClick={() => document.querySelector('[data-testid="roles-import-input"]')?.click()}
                                    data-testid="roles-import-menu">
                  <Upload className="w-4 h-4 mr-2" /> Import JSON bundle…
                </DropdownMenuItem>
                <DropdownMenuItem className="rounded-none cursor-pointer"
                                    onClick={async () => {
                                      try {
                                        const { data } = await api.post(`/workspaces/${activeWorkspaceId}/roles/sync-presets`);
                                        if (data.total_added === 0) toast.info("All preset roles are already present.");
                                        else { toast.success(`Added ${data.total_added} preset role${data.total_added === 1 ? "" : "s"}`); load(); }
                                      } catch (e) { toast.error(apiErrorMessage(e, "Sync failed")); }
                                    }} data-testid="roles-sync-presets-btn">
                  <Shield className="w-4 h-4 mr-2" /> Sync preset roles
                </DropdownMenuItem>
                <DropdownMenuItem className="rounded-none cursor-pointer"
                                    onClick={() => setShowCleanup(true)} data-testid="roles-cleanup-orphans-btn">
                  <Wrench className="w-4 h-4 mr-2" /> Cleanup orphan roles
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-foreground/50">
                  Duplicate from…
                </DropdownMenuLabel>
                {rows.length === 0 && (
                  <div className="px-3 py-1.5 text-[11px] text-foreground/60">No roles yet</div>
                )}
                {rows.slice(0, 20).map((r) => (
                  <DropdownMenuItem
                    key={r.id}
                    className="rounded-none cursor-pointer"
                    onClick={() => setEditing({
                      name: `Copy of ${r.name}`,
                      description: r.description || "",
                      permissions: [...(r.permissions || [])],
                      nav_overrides: { ...(r.nav_overrides || {}) },
                      default_route: r.default_route || null,
                      _clonedFrom: r.name,
                    })}
                    data-testid={`roles-duplicate-from-${r.slug}`}
                  >
                    <Copy className="w-3.5 h-3.5 mr-2" />
                    <span className="truncate">{r.name}</span>
                    <span className="ml-auto text-[10px] text-foreground/40">{(r.permissions || []).length}p</span>
                  </DropdownMenuItem>
                ))}
                {me?.is_super_admin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="rounded-none cursor-pointer text-amber-800"
                                        onClick={() => setSweepOpen(true)} data-testid="roles-orphan-sweep-btn">
                      <Shield className="w-4 h-4 mr-2" /> Orphan sweep · super-admin
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Hidden import input — triggered by the "Import JSON bundle" menu item. */}
            <input
              type="file"
              accept="application/json,.json"
              className="hidden"
              data-testid="roles-import-input"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                try {
                  const text = await file.text();
                  const bundle = JSON.parse(text);
                  if (bundle.version !== 1 || !Array.isArray(bundle.roles)) {
                    toast.error("Invalid role bundle — expected {version:1, roles:[…]}"); return;
                  }
                  const strategy = window.prompt(
                    `Load ${bundle.roles.length} role(s)?\n\nHow to handle roles that already exist here?\n\n  skip_existing    — leave existing roles alone (default, safest)\n  rename_on_conflict — import as '<slug>-imported-2'\n  overwrite        — replace existing role's perms\n\nType one of: skip_existing / rename_on_conflict / overwrite`,
                    "skip_existing",
                  );
                  if (!strategy) return;
                  const { data } = await api.post(`/workspaces/${activeWorkspaceId}/roles/import`, { ...bundle, strategy });
                  const bits = [];
                  if (data.imported.length) bits.push(`${data.imported.length} imported`);
                  if (data.renamed.length) bits.push(`${data.renamed.length} renamed`);
                  if (data.overwritten.length) bits.push(`${data.overwritten.length} overwritten`);
                  if (data.skipped.length) bits.push(`${data.skipped.length} skipped`);
                  toast.success(bits.join(", ") || "Nothing to import");
                  load();
                } catch (err) { toast.error(apiErrorMessage(err, "Import failed")); }
              }}
            />
          </div>
        )}
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-4">
        <div className="border border-primary/20 bg-primary/5 p-4 text-sm flex items-start gap-3" data-testid="roles-helper-banner">
          <Shield className="w-4 h-4 mt-0.5 text-primary shrink-0" />
          <div>
            Roles you see here apply <strong>only to this workspace</strong>. Each can be customised, renamed, or deleted (when no one is using it).
            The Super Admin role is platform-level and is not shown.
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
            <Input className="pl-8 h-9" placeholder="Search roles…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="roles-search-input" />
          </div>
          <div className="text-xs text-foreground/60 ml-auto">{rows.length} role{rows.length === 1 ? "" : "s"}</div>
        </div>

        <AdvancedTable
          tableKey="roles"
          title="Roles"
          rows={filtered}
          columns={roleColumns}
          loading={busy && rows.length === 0}
          emptyIcon={Shield}
          emptyTitle="No roles yet"
          emptyDescription="Create your first role."
          onRowClick={compareMode ? (r) => toggleCompareId(r.id) : (r) => setInspecting(r)}
          selectable={compareMode}
          selectedIds={compareIds}
          onSelectionChange={setCompareIds}
          renderBulkBar={compareMode ? (sel) => (
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold uppercase tracking-widest text-primary" data-testid="roles-compare-count">
                {sel.length} selected
              </span>
              <span className="text-foreground/60">Pick 2–10 roles then click Open compare</span>
              <Button size="sm" className="rounded-none h-8"
                        disabled={sel.length < 2 || sel.length > 10}
                        onClick={() => setCompareOpen(true)}
                        data-testid="roles-compare-open">
                Open compare &amp; edit →
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-8"
                        onClick={() => setCompareIds(new Set())} data-testid="roles-compare-clear">Clear</Button>
            </div>
          ) : undefined}
          renderRowActions={(r) => (
            <div className="flex gap-1 justify-end">
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-2"
                      onClick={(e) => { e.stopPropagation(); setAuditFor(r); }}
                      title="Role change history"
                      data-testid={`roles-audit-${r.slug}`}><History className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-2"
                      onClick={(e) => { e.stopPropagation(); setInspecting(r); }}
                      data-testid={`roles-inspect-${r.slug}`}><Eye className="w-3.5 h-3.5" /></Button>
              {isAdmin && (
                <>
                  <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" title={`Duplicate from ${r.name}`}
                          onClick={(e) => { e.stopPropagation(); setEditing({
                            name: `Copy of ${r.name}`,
                            description: r.description || "",
                            permissions: [...(r.permissions || [])],
                            // R125 · Preserve show/hide/auto nav prefs +
                            // default route when duplicating.
                            nav_overrides: { ...(r.nav_overrides || {}) },
                            default_route: r.default_route || null,
                            _clonedFrom: r.name,
                          }); }}
                          data-testid={`roles-duplicate-${r.slug}`}><Copy className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="rounded-none h-7 px-2"
                          onClick={(e) => { e.stopPropagation(); setEditing(r); }}
                          data-testid={`roles-edit-${r.slug}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                  {/* R158x · Feb 2026 — Deactivate / Activate. Deactivated roles
                      hide from role-pickers but existing assignments are preserved. */}
                  {!r.is_system && (
                    r.is_active === false ? (
                      <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-emerald-600 hover:text-emerald-700"
                              onClick={(e) => { e.stopPropagation(); activateRole(r); }}
                              title="Reactivate this role"
                              data-testid={`roles-activate-${r.slug}`}><Power className="w-3.5 h-3.5" /></Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-amber-600 hover:text-amber-700"
                              onClick={(e) => { e.stopPropagation(); handleDeactivate(r); }}
                              title={(r.assignee_count || 0) > 0
                                ? `Deactivate — will prompt to reassign ${r.assignee_count} holder(s) first`
                                : "Deactivate this role"}
                              data-testid={`roles-deactivate-${r.slug}`}><PowerOff className="w-3.5 h-3.5" /></Button>
                    )
                  )}
                  {!r.is_system && (r.assignee_count || 0) === 0 && (
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-destructive hover:text-destructive"
                            onClick={(e) => { e.stopPropagation(); remove(r); }}
                            data-testid={`roles-delete-${r.slug}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  )}
                </>
              )}
            </div>
          )}
        />
      </div>

      {editing && (
        <RoleEditor
          wsId={activeWorkspaceId}
          role={editing}
          groups={groups}
          modules={modules}
          existingRoles={rows}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {inspecting && (
        <RoleInspector
          wsId={activeWorkspaceId}
          role={inspecting}
          groups={groups}
          onClose={() => setInspecting(null)}
        />
      )}
      {auditFor && (
        <RoleAuditDrawer
          wsId={activeWorkspaceId}
          role={auditFor}
          onClose={() => setAuditFor(null)}
        />
      )}
      {/* R158x · Feb 2026 — Deactivate role → reassign holders first */}
      {deactivating && (
        <DeactivateRoleDialog
          wsId={activeWorkspaceId}
          role={deactivating}
          candidates={rows.filter((r) => r.id !== deactivating.id && r.is_active !== false)}
          onCancel={() => setDeactivating(null)}
          onDone={() => { setDeactivating(null); load(); }}
        />
      )}
      {/* R164a · Feb 2026 — Bulk-cleanup orphan role assignments.
          Fetches the /roles/orphans preview + reassigns to a picked target. */}
      {showCleanup && (
        <CleanupOrphansDialog
          wsId={activeWorkspaceId}
          candidates={rows.filter((r) => r.is_active !== false)}
          onClose={() => setShowCleanup(false)}
          onDone={() => { setShowCleanup(false); load(); }}
        />
      )}
      {tplPickerOpen && (
        <RoleTemplatePicker
          wsId={activeWorkspaceId}
          onClose={() => setTplPickerOpen(false)}
          onDone={() => { setTplPickerOpen(false); load(); }}
        />
      )}
      {sweepOpen && (
        <OrphanSweepDialog onClose={() => setSweepOpen(false)} />
      )}
      {compareOpen && (
        <RoleCompareEditor
          wsId={activeWorkspaceId}
          roles={rows.filter((r) => compareIds.has(r.id))}
          groups={groups}
          onClose={() => setCompareOpen(false)}
          onSaved={(opts) => {
            // Feb 2026 · Keep the compare dialog open after save so
            // admins can chain more edits. Just refresh the roles list
            // in the background.
            load();
            if (!opts?.keepOpen) {
              setCompareOpen(false); setCompareMode(false); setCompareIds(new Set());
            }
          }}
        />
      )}
    </>
  );
}

function RoleEditor({ wsId, role, groups, modules = [], existingRoles = [], onClose, onSaved }) {
  const isNew = !role?.id;
  const [name, setName] = useState(role.name || "");
  const [description, setDescription] = useState(role.description || "");
  const [perms, setPerms] = useState(new Set(role.permissions || []));
  const [navOverrides, setNavOverrides] = useState(role.nav_overrides || {});
  const [defaultRoute, setDefaultRoute] = useState(role.default_route || "");
  const [busy, setBusy] = useState(false);
  const [permQ, setPermQ] = useState("");
  const lockedToFull = role.slug === "workspace_owner";
  // Iter 142 · total number of catalog perms — used in the perm-count chip.
  const ALL_PERMS_COUNT = groups.reduce((n, g) => n + g.permissions.length, 0);

  const setOverride = (key, val) => {
    // R268jjjj · Feb 2026 — Guard against a NAV item shipped without a
    // testid ending up as an `undefined` key in nav_overrides (which
    // JS stringifies to the literal "undefined" and the backend then
    // rejects with `Invalid nav key: 'undefined' (must match nav-* or
    // header-*)`). Ignore any write with a non-matching key.
    if (!key || typeof key !== "string" || !/^(nav|header)-[a-z0-9][a-z0-9-]{0,60}$/.test(key)) return;
    setNavOverrides((o) => {
      const n = { ...o };
      if (val === "auto") delete n[key]; else n[key] = val;
      return n;
    });
  };

  // R268jjjj · Feb 2026 — Belt & braces: strip any historically-bad
  // keys before submit. Existing role docs may still carry a literal
  // "undefined" key from before this fix; without cleaning them the
  // validator would 400 on every save.
  const _NAV_KEY_RE = /^(nav|header)-[a-z0-9][a-z0-9-]{0,60}$/;
  const _cleanNavOverrides = (o) => {
    const out = {};
    for (const [k, v] of Object.entries(o || {})) {
      if (typeof k === "string" && _NAV_KEY_RE.test(k) && (v === "show" || v === "hide")) {
        out[k] = v;
      }
    }
    return out;
  };

  const toggle = (code) => {
    if (lockedToFull) return;
    setPerms((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
  };
  const toggleGroup = (codes, on) => {
    if (lockedToFull) return;
    setPerms((s) => { const n = new Set(s); codes.forEach((c) => { if (on) n.add(c); else n.delete(c); }); return n; });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name, description, permissions: Array.from(perms), nav_overrides: _cleanNavOverrides(navOverrides), default_route: defaultRoute || "" };
      if (isNew) await api.post(`/workspaces/${wsId}/roles`, body);
      else await api.patch(`/workspaces/${wsId}/roles/${role.id}`, body);
      toast.success(isNew ? "Role created" : "Role updated");
      onSaved?.();
    } catch (err) { toast.error(apiErrorMessage(err, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="role-editor-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Create role" : `Edit role · ${role.name}`}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Role name</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)} className="h-10" data-testid="role-editor-name" />
            </div>
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Description</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} className="h-10" data-testid="role-editor-description" />
            </div>
          </div>

          {lockedToFull && (
            <div className="border border-amber-500/40 bg-amber-500/5 p-3 text-xs" data-testid="role-editor-locked-banner">
              The <code>workspace_owner</code> role always holds every permission. You can rename it, but its permission set is fixed.
            </div>
          )}

          {role._clonedFrom && isNew && (
            <div className="border border-primary/30 bg-primary/5 p-3 text-xs flex items-center gap-2" data-testid="role-editor-clone-banner">
              <Copy className="w-3.5 h-3.5 text-primary" />
              <div>Cloning from <strong>{role._clonedFrom}</strong>. All its permissions are pre-selected — tweak as needed before saving.</div>
            </div>
          )}

          {isNew && !role._clonedFrom && existingRoles.length > 0 && (
            <div className="border border-border p-3 flex items-center gap-3 flex-wrap" data-testid="role-editor-copy-from">
              <Copy className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-medium">Start from an existing role&apos;s permissions?</span>
              <Select onValueChange={(v) => {
                const src = existingRoles.find((r) => r.slug === v);
                if (!src) return;
                setPerms(new Set(src.permissions || []));
                setNavOverrides({ ...(src.nav_overrides || {}) });
                if (src.default_route) setDefaultRoute(src.default_route);
                toast.success(`Copied ${src.permissions?.length || 0} permissions from ${src.name}`);
              }}>
                <SelectTrigger className="h-8 w-56 rounded-none text-xs" data-testid="role-editor-copy-from-select"><SelectValue placeholder="Pick a role template…" /></SelectTrigger>
                <SelectContent>
                  {existingRoles.map((r) => (
                    <SelectItem key={r.slug} value={r.slug}>{r.name} · {(r.permissions || []).length} perms</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[10px] text-muted-foreground ml-auto">Or start blank and pick perms below.</span>
            </div>
          )}

          {/* Iter 236c · Live tab visibility preview. Shows admins exactly what
              this role sees on a Brand page + which sensitive columns (rates,
              costs, margins, billing) will render — before they save. */}
          <RolePreviewPanel perms={perms} onJumpTo={(code) => setPermQ(code)} />

          <div className="space-y-4" data-testid="role-editor-perms">
            <div className="flex items-center gap-2 sticky top-0 bg-background z-10 py-2 -mx-1 px-1 border-b border-border/50 flex-wrap">
              <div className="relative flex-1 min-w-[240px] max-w-md">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
                <Input
                  className="pl-8 h-9 text-xs"
                  placeholder="Search permissions… (name or code)"
                  value={permQ}
                  onChange={(e) => setPermQ(e.target.value)}
                  data-testid="role-editor-perm-search"
                />
              </div>
              {!lockedToFull && (
                <>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-widest ml-2">Bulk</span>
                  <Button
                    type="button" size="sm" variant="outline"
                    className="rounded-none h-8 text-[11px]"
                    onClick={() => {
                      // Grant ALL visible view/read/list permissions (leave create/manage/delete alone).
                      const q = permQ.trim().toLowerCase();
                      const next = new Set(perms);
                      const isReadOnly = (code) => /(\.view|\.read|\.list)(\.|$)/.test(code) || code.endsWith(".search");
                      for (const g of groups) {
                        for (const p of g.permissions) {
                          const label = p.label.toLowerCase();
                          const matches = !q || label.includes(q) || p.code.toLowerCase().includes(q);
                          if (matches && isReadOnly(p.code)) next.add(p.code);
                        }
                      }
                      setPerms(next);
                    }}
                    data-testid="role-editor-grant-readonly"
                    title="Grant every 'view / read / list' permission that matches your current search"
                  >Read-only</Button>
                  <Button
                    type="button" size="sm" variant="outline"
                    className="rounded-none h-8 text-[11px]"
                    onClick={() => {
                      // Grant EVERY currently-visible permission.
                      const q = permQ.trim().toLowerCase();
                      const next = new Set(perms);
                      for (const g of groups) {
                        for (const p of g.permissions) {
                          const label = p.label.toLowerCase();
                          if (!q || label.includes(q) || p.code.toLowerCase().includes(q)) next.add(p.code);
                        }
                      }
                      setPerms(next);
                    }}
                    data-testid="role-editor-grant-all-visible"
                  >Grant all{permQ ? " visible" : ""}</Button>
                  <Button
                    type="button" size="sm" variant="ghost"
                    className="rounded-none h-8 text-[11px] text-destructive hover:text-destructive"
                    onClick={() => {
                      // Clear every currently-visible permission (respects filter).
                      const q = permQ.trim().toLowerCase();
                      const next = new Set(perms);
                      for (const g of groups) {
                        for (const p of g.permissions) {
                          const label = p.label.toLowerCase();
                          if (!q || label.includes(q) || p.code.toLowerCase().includes(q)) next.delete(p.code);
                        }
                      }
                      setPerms(next);
                    }}
                    data-testid="role-editor-clear-visible"
                  >Clear{permQ ? " visible" : " all"}</Button>
                </>
              )}
              <div className="text-[11px] text-foreground/60 ml-auto">
                <span data-testid="role-editor-perm-count" className="font-bold text-primary">{perms.size}</span>/{ALL_PERMS_COUNT} · {(modules && modules.length) ? `${modules.length} modules · ${groups.length} screens` : `${groups.length} modules`}
              </div>
              {permQ && (
                <button type="button" onClick={() => setPermQ("")} className="text-[11px] text-foreground/60 hover:text-foreground underline" data-testid="role-editor-perm-search-clear">Clear filter</button>
              )}
            </div>
            {(() => {
              const q = permQ.trim().toLowerCase();
              // Feb 2026 · Module → Screen hierarchy render. Falls back
              // to the legacy flat-groups accordion when the /permissions
              // endpoint hasn't served `modules` yet (e.g. old cached
              // response). Filtering matches perm code OR label OR the
              // screen/module label so users can search "finance" and
              // pin down the whole module.
              const filterPerms = (permsArr) => q
                ? permsArr.filter((p) => p.label.toLowerCase().includes(q) || p.code.toLowerCase().includes(q))
                : permsArr;

              if (modules && modules.length > 0) {
                const visibleModules = modules.map((m) => {
                  const moduleMatches = q && m.label.toLowerCase().includes(q);
                  const screens = m.screens.map((s) => {
                    const screenMatches = q && s.label.toLowerCase().includes(q);
                    const permissions = (moduleMatches || screenMatches) ? s.permissions : filterPerms(s.permissions);
                    return { ...s, permissions };
                  }).filter((s) => s.permissions.length > 0);
                  return { ...m, screens };
                }).filter((m) => m.screens.length > 0);
                if (q && visibleModules.length === 0) {
                  return <div className="text-xs text-foreground/60 border border-dashed border-border p-4 text-center" data-testid="role-editor-perm-search-empty">No permissions match &ldquo;{permQ}&rdquo;.</div>;
                }
                const openModules = q ? visibleModules.map((m) => m.label) : [];
                return (
                  <Accordion key={q || "_none_"} type="multiple" defaultValue={openModules} className="border border-border divide-y divide-border">
                    {visibleModules.map((m) => {
                      const moduleCodes = m.screens.flatMap((s) => s.permissions.map((p) => p.code));
                      const moduleGranted = moduleCodes.filter((c) => perms.has(c)).length;
                      const moduleAllOn = moduleGranted === moduleCodes.length;
                      const modulePartiallyOn = moduleGranted > 0 && !moduleAllOn;
                      return (
                        <AccordionItem key={m.label} value={m.label} className="border-b-0" data-testid={`role-editor-module-${m.label}`}>
                          <div className="flex items-center gap-2 pl-4 bg-primary/5">
                            {!lockedToFull && (
                              <input
                                type="checkbox"
                                checked={moduleAllOn}
                                ref={(el) => { if (el) el.indeterminate = modulePartiallyOn; }}
                                onChange={(e) => { e.stopPropagation(); toggleGroup(moduleCodes, !moduleAllOn); }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-3.5 h-3.5"
                                aria-label={`Select all in ${m.label}`}
                                data-testid={`role-editor-module-selectall-${m.label}`}
                                title="Select / clear all screens in this module"
                              />
                            )}
                            <AccordionTrigger className="flex-1 py-3 hover:no-underline" data-testid={`role-editor-module-toggle-${m.label}`}>
                              <span className="flex items-center gap-2">
                                <span className="font-bold text-[15px] text-primary">{m.label}</span>
                                <span className={`text-xs ${moduleAllOn ? "text-primary font-bold" : moduleGranted ? "text-primary/70" : "text-foreground/50"}`}>
                                  {moduleGranted}/{moduleCodes.length}
                                </span>
                                <span className="text-[10px] text-foreground/50 hidden sm:inline">· {m.screens.length} screens</span>
                              </span>
                            </AccordionTrigger>
                            {!lockedToFull && (
                              <div className="flex items-center gap-0.5 pr-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                                <button type="button" className="text-[10px] px-1.5 py-0.5 border border-border hover:bg-primary/10 hover:border-primary transition-colors"
                                        onClick={() => {
                                          const readOnly = moduleCodes.filter((c) => /(\.view|\.read|\.list)(\.|$)/.test(c) || c.endsWith(".search"));
                                          toggleGroup(readOnly, true);
                                        }}
                                        title="Grant every view/read perm in this module"
                                        data-testid={`role-editor-module-readonly-${m.label}`}>Read</button>
                                <button type="button" className="text-[10px] px-1.5 py-0.5 border border-border hover:bg-primary/10 hover:border-primary transition-colors"
                                        onClick={() => toggleGroup(moduleCodes, true)}
                                        title="Grant every permission in this module"
                                        data-testid={`role-editor-module-full-${m.label}`}>Full</button>
                                <button type="button" className="text-[10px] px-1.5 py-0.5 border border-border hover:bg-destructive/10 hover:border-destructive text-destructive transition-colors"
                                        onClick={() => toggleGroup(moduleCodes, false)}
                                        title="Clear every permission in this module"
                                        data-testid={`role-editor-module-none-${m.label}`}>None</button>
                              </div>
                            )}
                          </div>
                          <AccordionContent className="pb-0">
                            <div className="pl-4 pr-1 py-1 bg-muted/20 space-y-0">
                              <Accordion type="multiple" defaultValue={q ? m.screens.map((s) => s.label) : []} key={q || "_screens_"}>
                              {m.screens.map((s) => {
                                const codes = s.permissions.map((p) => p.code);
                                const granted = codes.filter((c) => perms.has(c)).length;
                                const allOn = granted === codes.length;
                                const partiallyOn = granted > 0 && !allOn;
                                return (
                                  <AccordionItem key={s.label} value={s.label}
                                                    className="border border-border/60 mb-1 bg-background"
                                                    data-testid={`role-editor-screen-${s.label}`}>
                                    <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 border-b border-border/60">
                                      {!lockedToFull && (
                                        <input
                                          type="checkbox"
                                          checked={allOn}
                                          ref={(el) => { if (el) el.indeterminate = partiallyOn; }}
                                          onChange={(e) => { e.stopPropagation(); toggleGroup(codes, !allOn); }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="w-3.5 h-3.5"
                                          aria-label={`Select all in ${s.label}`}
                                          data-testid={`role-editor-screen-selectall-${s.label}`}
                                        />
                                      )}
                                      <AccordionTrigger className="flex-1 py-0 hover:no-underline"
                                                            data-testid={`role-editor-screen-toggle-${s.label}`}>
                                        <span className="flex items-center gap-2">
                                          <span className="font-semibold text-[13px]">{s.label}</span>
                                          <span className={`text-[11px] ${allOn ? "text-primary font-bold" : granted ? "text-primary/70" : "text-foreground/50"}`}>
                                            {granted}/{codes.length}
                                          </span>
                                        </span>
                                      </AccordionTrigger>
                                      {!lockedToFull && (
                                        <div className="flex items-center gap-0.5 shrink-0"
                                             onClick={(e) => e.stopPropagation()}>
                                          <button type="button" className="text-[10px] px-1.5 py-0.5 border border-border hover:bg-primary/10 hover:border-primary transition-colors"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    const ro = codes.filter((c) => /(\.view|\.read|\.list)(\.|$)/.test(c) || c.endsWith(".search"));
                                                    toggleGroup(ro, true);
                                                  }}
                                                  title="Grant view/read perms on this screen"
                                                  data-testid={`role-editor-screen-readonly-${s.label}`}>Read</button>
                                          <button type="button" className="text-[10px] px-1.5 py-0.5 border border-border hover:bg-primary/10 hover:border-primary transition-colors"
                                                  onClick={(e) => { e.stopPropagation(); toggleGroup(codes, true); }}
                                                  data-testid={`role-editor-screen-full-${s.label}`}>Full</button>
                                          <button type="button" className="text-[10px] px-1.5 py-0.5 border border-border hover:bg-destructive/10 hover:border-destructive text-destructive transition-colors"
                                                  onClick={(e) => { e.stopPropagation(); toggleGroup(codes, false); }}
                                                  data-testid={`role-editor-screen-none-${s.label}`}>None</button>
                                        </div>
                                      )}
                                    </div>
                                    <AccordionContent className="pb-0">
                                      <div className="grid sm:grid-cols-2 gap-px bg-border/60">
                                        {s.permissions.map((p) => (
                                          <label key={p.code} className={`flex items-start gap-2 px-3 py-2 text-sm bg-background ${lockedToFull ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-muted/40"}`}>
                                            <input type="checkbox" disabled={lockedToFull} checked={perms.has(p.code)} onChange={() => toggle(p.code)} className="mt-0.5 w-4 h-4" data-testid={`role-perm-${p.code}`} />
                                            <span className="min-w-0 flex-1"><strong className="block leading-snug text-[13px]">{p.label}</strong><code className="text-[10px] text-foreground/60 font-mono block mt-0.5 break-all">{p.code}</code></span>
                                          </label>
                                        ))}
                                      </div>
                                    </AccordionContent>
                                  </AccordionItem>
                                );
                              })}
                              </Accordion>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                );
              }

              // Legacy flat-groups fallback (unchanged from prior version).
              const visibleGroups = groups.map((g) => ({
                ...g,
                permissions: filterPerms(g.permissions),
              })).filter((g) => g.permissions.length > 0);
              if (q && visibleGroups.length === 0) {
                return <div className="text-xs text-foreground/60 border border-dashed border-border p-4 text-center" data-testid="role-editor-perm-search-empty">No permissions match &ldquo;{permQ}&rdquo;.</div>;
              }
              // Auto-expand groups when the user is filtering, otherwise
              // default to nothing open — one click per module.
              const openValues = q ? visibleGroups.map((g) => g.label) : [];
              return (
                // key={q} forces re-mount when the search term changes so
                // Radix's uncontrolled Accordion re-reads defaultValue and
                // opens the matching modules (Iter 140 bugfix).
                <Accordion key={q || "_none_"} type="multiple" defaultValue={openValues} className="border border-border divide-y divide-border">
                  {visibleGroups.map((g) => {
                    const codes = g.permissions.map((p) => p.code);
                    const granted = codes.filter((c) => perms.has(c)).length;
                    const allOn = granted === codes.length;
                    const partiallyOn = granted > 0 && !allOn;
                    return (
                      <AccordionItem key={g.label} value={g.label} className="border-b-0" data-testid={`role-editor-group-${g.label}`}>
                        <div className="flex items-center gap-2 pl-4 bg-muted/40">
                          {!lockedToFull && (
                            <input
                              type="checkbox"
                              checked={allOn}
                              ref={(el) => { if (el) el.indeterminate = partiallyOn; }}
                              onChange={(e) => { e.stopPropagation(); toggleGroup(codes, !allOn); }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-3.5 h-3.5"
                              aria-label={`Select all in ${g.label}`}
                              data-testid={`role-editor-group-selectall-${g.label}`}
                              title="Select / clear all in module"
                            />
                          )}
                          <AccordionTrigger className="flex-1 py-2.5 hover:no-underline" data-testid={`role-editor-group-toggle-${g.label}`}>
                            <span className="flex items-center gap-2">
                              <span className="font-bold text-[15px]">{g.label}</span>
                              <span className={`text-xs ${allOn ? "text-primary font-bold" : granted ? "text-primary/70" : "text-foreground/50"}`}>
                                {granted}/{codes.length}
                              </span>
                            </span>
                          </AccordionTrigger>
                          {!lockedToFull && (
                            <div className="flex items-center gap-0.5 pr-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                              <button type="button" className="text-[10px] px-1.5 py-0.5 border border-border hover:bg-primary/10 hover:border-primary transition-colors"
                                      onClick={() => {
                                        const readOnlyCodes = g.permissions
                                          .filter((p) => /(\.view|\.read|\.list)(\.|$)/.test(p.code) || p.code.endsWith(".search"))
                                          .map((p) => p.code);
                                        toggleGroup(readOnlyCodes, true);
                                      }}
                                      title="Grant view/read/list-style permissions only"
                                      data-testid={`role-editor-group-readonly-${g.label}`}>Read</button>
                              <button type="button" className="text-[10px] px-1.5 py-0.5 border border-border hover:bg-primary/10 hover:border-primary transition-colors"
                                      onClick={() => toggleGroup(codes, true)}
                                      title="Grant every permission in this module"
                                      data-testid={`role-editor-group-full-${g.label}`}>Full</button>
                              <button type="button" className="text-[10px] px-1.5 py-0.5 border border-border hover:bg-destructive/10 hover:border-destructive text-destructive transition-colors"
                                      onClick={() => toggleGroup(codes, false)}
                                      title="Clear every permission in this module"
                                      data-testid={`role-editor-group-none-${g.label}`}>None</button>
                            </div>
                          )}
                        </div>
                        <AccordionContent className="pb-0">
                          <div className="grid sm:grid-cols-2 gap-px bg-border">
                            {g.permissions.map((p) => (
                              <label key={p.code} className={`flex items-start gap-2 px-3 py-2.5 text-sm bg-background ${lockedToFull ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-muted/40"}`}>
                                <input type="checkbox" disabled={lockedToFull} checked={perms.has(p.code)} onChange={() => toggle(p.code)} className="mt-0.5 w-4 h-4" data-testid={`role-perm-${p.code}`} />
                                <span className="min-w-0 flex-1"><strong className="block leading-snug text-[13px]">{p.label}</strong><code className="text-[11px] text-foreground/60 font-mono block mt-0.5 break-all">{p.code}</code></span>
                              </label>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              );
            })()}
          </div>

          <div className="border border-border" data-testid="role-editor-visibility">
            <div className="flex items-center justify-between px-4 py-2 bg-muted/40 flex-wrap gap-2">
              <div className="font-semibold text-sm flex items-center gap-2"><PanelLeft className="w-4 h-4 text-primary" />Navigation visibility & landing page</div>
              <div className="flex items-center gap-1">
                {/* Iter 246 · Matrix-level bulk actions */}
                <button
                  type="button"
                  onClick={() => {
                    const next = {};
                    // R268jjjj · Feb 2026 — Skip any item without a
                    // valid `key` / `testid` so we never write an
                    // "undefined" key that the backend would reject.
                    HEADER_ITEMS.forEach((h) => { if (h.key && _NAV_KEY_RE.test(h.key)) next[h.key] = "show"; });
                    NAV_GROUPS.forEach((g) => g.items.forEach((n) => { if (n.testid && _NAV_KEY_RE.test(n.testid)) next[n.testid] = "show"; }));
                    setNavOverrides(next);
                  }}
                  className="text-[10px] px-2 py-0.5 border border-border hover:bg-emerald-500/10 hover:border-emerald-500 hover:text-emerald-700 transition-colors"
                  title="Show every menu item for this role"
                  data-testid="role-editor-visibility-showall"
                >Show all</button>
                <button
                  type="button"
                  onClick={() => {
                    const next = {};
                    HEADER_ITEMS.forEach((h) => { if (h.key && _NAV_KEY_RE.test(h.key)) next[h.key] = "hide"; });
                    NAV_GROUPS.forEach((g) => g.items.forEach((n) => { if (n.testid && _NAV_KEY_RE.test(n.testid)) next[n.testid] = "hide"; }));
                    setNavOverrides(next);
                  }}
                  className="text-[10px] px-2 py-0.5 border border-border hover:bg-rose-500/10 hover:border-rose-500 hover:text-rose-700 transition-colors"
                  title="Hide every menu item for this role"
                  data-testid="role-editor-visibility-hideall"
                >Hide all</button>
                <button
                  type="button"
                  onClick={() => setNavOverrides({})}
                  className="text-[10px] px-2 py-0.5 border border-border hover:bg-primary/10 hover:border-primary transition-colors"
                  title="Reset every item to Auto (permission-derived)"
                  data-testid="role-editor-visibility-clearall"
                >Clear all (Auto)</button>
                <span className="text-[10px] text-foreground/50 ml-2">Auto = derived from permissions</span>
              </div>
            </div>
            <div className="p-4 space-y-4">
              <div className="max-w-sm">
                <Label className="label-eyebrow mb-2 inline-block">Default landing page for this role</Label>
                <Select value={defaultRoute || "none"} onValueChange={(v) => setDefaultRoute(v === "none" ? "" : v)}>
                  <SelectTrigger className="h-9 rounded-none text-xs" data-testid="role-editor-landing"><SelectValue placeholder="Default (Bulletin board)" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">Default (Bulletin board)</SelectItem>
                    {LANDING_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <VisibilitySection
                label="Header items"
                sectionKey="header"
                items={HEADER_ITEMS.map((h) => ({ key: h.key, label: h.label }))}
                navOverrides={navOverrides}
                setNavOverrides={setNavOverrides}
                onChange={setOverride}
              />
              {NAV_GROUPS.map((g) => (
                <VisibilitySection
                  key={g.key}
                  label={`${g.label} — sidebar`}
                  sectionKey={g.key}
                  items={g.items.map((n) => ({ key: n.testid, label: n.label }))}
                  navOverrides={navOverrides}
                  setNavOverrides={setNavOverrides}
                  onChange={setOverride}
                />
              ))}
              <p className="text-[10px] text-foreground/50">“Show” only unhides the menu item — API access is still controlled by the permissions above. “Hide” removes the item from the sidebar/header for everyone with this role.</p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-none" onClick={onClose} data-testid="role-editor-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-none" data-testid="role-editor-save">{busy ? "Saving…" : isNew ? "Create" : "Save changes"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Feb 2026 · Small dropdown next to each role's chip in the compare
// header. One click clones this role's permission set onto another
// role in the set — great for "make Editor look like Manager, then
// tweak one perm off". Never persists on click; the compare's dirty
// state kicks in and admins commit via "Save all".
function RoleCopyMenu({ current, others, onCopy }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ml-1 text-[9px] px-1 py-0.5 border border-border hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-0.5"
        title={`Copy ${current.name}'s permissions onto another role`}
        data-testid={`role-compare-copy-${current.slug}`}>
        <Copy className="w-3 h-3" /> Copy &rarr;
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[180px] border border-border bg-background shadow-lg" data-testid={`role-compare-copy-menu-${current.slug}`}>
          <div className="px-2 py-1 text-[9px] uppercase tracking-widest text-muted-foreground border-b border-border">
            Copy onto&hellip;
          </div>
          {others.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => { onCopy(o.id); setOpen(false); }}
              className="w-full text-left text-xs px-2 py-1.5 hover:bg-primary/10 hover:text-primary"
              data-testid={`role-compare-copy-target-${current.slug}-${o.slug}`}>
              {o.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}



// Feb 2026 · Side-by-side compare & edit for 2–10 roles.
// Renders a table of every permission grouped by Module → Screen, with
// one checkbox per role per row. "Save all" commits each role's diff
// atomically via the existing PATCH endpoint.
function RoleCompareEditor({ wsId, roles, groups, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  // Feb 2026 · Filter chips to hide rows where no role has the perm OR
  // every role has it — keeps the compare view focused on the rows that
  // actually differ / matter.
  const [hideNone, setHideNone] = useState(false);
  const [hideAll, setHideAll] = useState(false);
  // Local edit state — one Set of perm codes per role id. Kept in a
  // ref-friendly shape so we can rehydrate after a save without closing
  // the dialog (user can chain edits).
  const [rolesRef, setRolesRef] = useState(roles);
  const [state, setState] = useState(() => {
    const m = {};
    for (const r of roles) m[r.id] = new Set(r.permissions || []);
    return m;
  });
  const toggle = (roleId, code) => setState((s) => {
    const next = new Set(s[roleId]);
    next.has(code) ? next.delete(code) : next.add(code);
    return { ...s, [roleId]: next };
  });
  const bulkForRole = (roleId, codes, on) => setState((s) => {
    const next = new Set(s[roleId]);
    codes.forEach((c) => { if (on) next.add(c); else next.delete(c); });
    return { ...s, [roleId]: next };
  });
  // Feb 2026 · Copy permissions between two roles in the compare set.
  // One-click clones the entire permission set from `srcId` onto
  // `dstId` so admins can "clone-then-tweak" instead of ticking every
  // checkbox by hand. Emits a toast noting what was copied.
  const copyPermsFromTo = (srcId, dstId) => {
    if (srcId === dstId) return;
    const src = rolesRef.find((r) => r.id === srcId);
    const dst = rolesRef.find((r) => r.id === dstId);
    if (!src || !dst) return;
    const srcSet = new Set(state[srcId] || []);
    setState((s) => ({ ...s, [dstId]: new Set(srcSet) }));
    toast.success(`Copied ${srcSet.size} permissions from ${src.name} → ${dst.name} · Save to persist`);
  };
  const dirty = (r) => {
    const orig = new Set(r.permissions || []);
    const cur = state[r.id];
    if (!cur) return false;
    if (orig.size !== cur.size) return true;
    for (const c of cur) if (!orig.has(c)) return true;
    return false;
  };
  const dirtyRoles = rolesRef.filter(dirty);
  const save = async () => {
    if (dirtyRoles.length === 0) { toast.info("No changes to save"); return; }
    setBusy(true);
    try {
      // PATCH each dirty role and capture the fresh server copy so the
      // dialog's baseline is realigned. Keeping the dialog open lets
      // the user chain more edits without a full round-trip.
      const updated = [];
      for (const r of dirtyRoles) {
        const { data } = await api.patch(`/workspaces/${wsId}/roles/${r.id}`, {
          name: r.name,
          description: r.description || "",
          permissions: Array.from(state[r.id]),
        });
        updated.push(data || { ...r, permissions: Array.from(state[r.id]) });
      }
      // Rebase local roles + state so `dirty()` resets to false.
      const map = new Map(updated.map((u) => [u.id, u]));
      const newRoles = rolesRef.map((r) => map.get(r.id) || r);
      setRolesRef(newRoles);
      setState((s) => {
        const next = { ...s };
        for (const u of updated) next[u.id] = new Set(u.permissions || []);
        return next;
      });
      toast.success(`Saved ${dirtyRoles.length} role${dirtyRoles.length === 1 ? "" : "s"} · you can keep editing`);
      // Notify parent so the roles list refreshes — but DON'T close the
      // dialog. `onSaved` is now a background refresh signal, not a
      // close-and-reload event.
      onSaved?.({ keepOpen: true });
    } catch (err) { toast.error(apiErrorMessage(err, "Save failed")); }
    finally { setBusy(false); }
  };
  const search = q.trim().toLowerCase();
  // Feb 2026 · Apply search + none/all filters. `none` means no role in
  // the current set has the perm; `all` means every role has it. Both
  // filters can be enabled simultaneously — the surviving rows are
  // then the ones with mixed grants (the interesting ones to compare).
  const roleIds = rolesRef.map((r) => r.id);
  const countOn = (code) => roleIds.reduce((n, id) => n + ((state[id] && state[id].has(code)) ? 1 : 0), 0);
  const visibleGroups = groups
    .map((g) => ({
      ...g,
      permissions: g.permissions.filter((p) => {
        if (search && !(p.label.toLowerCase().includes(search) || p.code.toLowerCase().includes(search))) return false;
        if (hideNone || hideAll) {
          const on = countOn(p.code);
          if (hideNone && on === 0) return false;
          if (hideAll && on === roleIds.length) return false;
        }
        return true;
      }),
    }))
    .filter((g) => g.permissions.length > 0);
  // Highlight rows where roles disagree so admins can eye differences quickly.
  const roleDiff = (code) => {
    const on = rolesRef.map((r) => state[r.id]?.has(code));
    return on.some((v) => v !== on[0]);
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-[95vw] max-h-[92vh] overflow-hidden flex flex-col rounded-none" data-testid="role-compare-editor">
        <DialogHeader>
          <DialogTitle>Compare &amp; edit · {rolesRef.length} roles</DialogTitle>
          <DialogDescription className="text-xs">
            Check any cell to grant that permission to that role. Rows where roles disagree are highlighted.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 py-2 border-b border-border flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
            <Input className="pl-8 h-9 text-xs" placeholder="Filter permissions (name or code)…"
                    value={q} onChange={(e) => setQ(e.target.value)}
                    data-testid="role-compare-search" />
          </div>
          {/* Feb 2026 · Compare-focus filters. Hide unanimous rows so
              you only stare at what actually differs across roles. */}
          <div className="flex items-center gap-1.5" data-testid="role-compare-filters">
            <button type="button"
              onClick={() => setHideNone((v) => !v)}
              className={`text-[10px] px-2 py-1 border transition-colors ${hideNone ? "bg-primary/10 border-primary text-primary" : "border-border hover:border-primary/60"}`}
              title="Hide permissions no role in this set has"
              data-testid="role-compare-hide-none">
              Hide none-have
            </button>
            <button type="button"
              onClick={() => setHideAll((v) => !v)}
              className={`text-[10px] px-2 py-1 border transition-colors ${hideAll ? "bg-primary/10 border-primary text-primary" : "border-border hover:border-primary/60"}`}
              title="Hide permissions every role in this set already has"
              data-testid="role-compare-hide-all">
              Hide all-have
            </button>
          </div>
          <div className="ml-auto flex items-center gap-3 text-[11px]">
            {rolesRef.map((r) => (
              <div key={r.id} className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${dirty(r) ? "bg-amber-500" : "bg-muted-foreground/40"}`}></span>
                <span className="font-semibold">{r.name}</span>
                <span className="text-foreground/50">{state[r.id]?.size ?? 0}p</span>
                {/* Feb 2026 · Copy this role's perms onto another. */}
                {rolesRef.length > 1 && (
                  <RoleCopyMenu current={r} others={rolesRef.filter((o) => o.id !== r.id)}
                                onCopy={(dstId) => copyPermsFromTo(r.id, dstId)} />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[10px] text-muted-foreground min-w-[280px]">Permission</th>
                {rolesRef.map((r) => (
                  <th key={r.id} className={`text-center px-2 py-2 font-semibold uppercase tracking-widest text-[10px] text-muted-foreground ${rolesRef.length > 6 ? "min-w-[92px]" : "min-w-[140px]"}`}
                      data-testid={`role-compare-col-${r.slug}`}>
                    {r.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleGroups.map((g) => {
                const codes = g.permissions.map((p) => p.code);
                return (
                  <React.Fragment key={g.label}>
                    <tr className="bg-primary/5">
                      <td className="px-3 py-2 font-bold text-primary text-[11px] uppercase tracking-widest">{g.label}</td>
                      {rolesRef.map((r) => {
                        const granted = codes.filter((c) => state[r.id]?.has(c)).length;
                        const allOn = granted === codes.length;
                        return (
                          <td key={r.id} className="text-center px-3 py-2">
                            <div className="inline-flex items-center gap-1">
                              <span className="text-[10px] text-foreground/60">{granted}/{codes.length}</span>
                              <button type="button" onClick={() => bulkForRole(r.id, codes, !allOn)}
                                        className="text-[10px] px-1.5 py-0.5 border border-border hover:bg-primary/10 hover:border-primary transition-colors"
                                        data-testid={`role-compare-bulk-${r.slug}-${g.label}`}>
                                {allOn ? "None" : "All"}
                              </button>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {g.permissions.map((p) => {
                      const diff = roleDiff(p.code);
                      return (
                        <tr key={p.code} className={`border-t border-border ${diff ? "bg-amber-50/40" : ""}`}>
                          <td className="px-3 py-1.5">
                            <div className="font-medium">{p.label}</div>
                            <code className="text-[10px] text-foreground/50 font-mono">{p.code}</code>
                          </td>
                          {rolesRef.map((r) => (
                            <td key={r.id} className="text-center px-3 py-1.5">
                              <input type="checkbox" checked={state[r.id]?.has(p.code) || false}
                                      onChange={() => toggle(r.id, p.code)}
                                      className="w-4 h-4"
                                      data-testid={`role-compare-cell-${r.slug}-${p.code}`} />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {visibleGroups.length === 0 && (
                <tr><td colSpan={1 + rolesRef.length} className="text-center py-10 text-muted-foreground italic">No permissions match the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <div className="flex items-center gap-2 w-full">
            <span className="text-xs text-muted-foreground">
              {dirtyRoles.length === 0 ? "No unsaved changes" : `${dirtyRoles.length} role${dirtyRoles.length === 1 ? "" : "s"} changed`}
            </span>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy} data-testid="role-compare-done">
                {dirtyRoles.length ? "Discard & close" : "Done"}
              </Button>
              <Button className="rounded-none" onClick={save}
                        disabled={busy || dirtyRoles.length === 0}
                        data-testid="role-compare-save">
                {busy ? "Saving…" : `Save all${dirtyRoles.length ? ` (${dirtyRoles.length})` : ""}`}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function VisibilitySection({ label, sectionKey, items, navOverrides, setNavOverrides, onChange }) {
  // R268jjjj · Feb 2026 — Drop any item that ships without a valid key
  // (e.g. a NAV entry that forgot its `testid` in a prior deploy) so
  // the UI never renders a row that would produce an "undefined" nav
  // key on toggle.
  items = (items || []).filter((it) => it && typeof it.key === "string" && /^(nav|header)-[a-z0-9][a-z0-9-]{0,60}$/.test(it.key));
  // Iter 246 · Section-level bulk toggle. Counts how many items in this
  // section share the same override so the master button can reflect state.
  const counts = items.reduce((acc, it) => {
    const v = navOverrides[it.key] || "auto";
    acc[v] = (acc[v] || 0) + 1; return acc;
  }, {});
  const setSection = (val) => {
    setNavOverrides((prev) => {
      const next = { ...prev };
      items.forEach((it) => {
        // R268jjjj · Feb 2026 — Skip items without a valid key so we
        // never write `next[undefined]` (JS stringifies to the literal
        // "undefined" and the backend then rejects the whole save).
        if (!it.key || typeof it.key !== "string") return;
        if (val === "auto") delete next[it.key];
        else next[it.key] = val;
      });
      return next;
    });
  };
  const btn = (val, activeCount, colorClass, testid, title) => (
    <button
      type="button"
      onClick={() => setSection(val)}
      className={`text-[10px] px-2 py-0.5 border border-border transition-colors ${colorClass}`}
      title={title}
      data-testid={testid}
    >
      {val === "auto" ? "Auto" : val === "show" ? "Show" : "Hide"} all
      <span className="ml-1 text-[9px] opacity-60">({activeCount}/{items.length})</span>
    </button>
  );
  return (
    <div data-testid={`visibility-section-${sectionKey}`}>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <Label className="label-eyebrow inline-block">{label}</Label>
        <div className="flex items-center gap-1">
          {btn("show", counts.show || 0, "hover:bg-emerald-500/10 hover:border-emerald-500 hover:text-emerald-700", `visibility-section-${sectionKey}-showall`, `Show every item in ${label}`)}
          {btn("hide", counts.hide || 0, "hover:bg-rose-500/10 hover:border-rose-500 hover:text-rose-700", `visibility-section-${sectionKey}-hideall`, `Hide every item in ${label}`)}
          {btn("auto", counts.auto || 0, "hover:bg-primary/10 hover:border-primary", `visibility-section-${sectionKey}-autoall`, "Reset every item to Auto")}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-px bg-border">
        {items.map((it) => (
          <VisibilityRow key={it.key} navKey={it.key} label={it.label} value={navOverrides[it.key] || "auto"} onChange={onChange} />
        ))}
      </div>
    </div>
  );
}

function VisibilityRow({ navKey, label, value, onChange }) {
  const opts = ["auto", "show", "hide"];
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-background" data-testid={`visibility-row-${navKey}`}>
      <span className="text-xs flex-1 truncate">{label}</span>
      <div className="flex border border-border">
        {opts.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => onChange(navKey, o)}
            data-active={value === o ? "true" : "false"}
            className={`px-2 py-0.5 text-[10px] capitalize transition-colors ${value === o
              ? (o === "hide" ? "bg-red-500 text-white" : o === "show" ? "bg-emerald-600 text-white" : "bg-foreground text-background")
              : "text-foreground/60 hover:bg-muted"}`}
            data-testid={`visibility-${navKey}-${o}`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function RoleInspector({ wsId, role, groups, onClose }) {
  const [data, setData] = useState(null);
  const [permQ, setPermQ] = useState("");
  useEffect(() => {
    api.get(`/workspaces/${wsId}/roles/${role.id}/inspect`).then((r) => setData(r.data));
  }, [wsId, role.id]);

  const q = permQ.trim().toLowerCase();
  const totalGranted = (data?.permissions || []).length;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="role-inspector-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Shield className="w-4 h-4" /> {role.name} <code className="text-xs font-mono text-foreground/60">{role.slug}</code></DialogTitle>
        </DialogHeader>
        {!data ? <div className="text-sm text-foreground/60">Loading…</div> : (
          <div className="space-y-5">
            <p className="text-sm text-foreground/70">{role.description || "—"}</p>
            <div>
              <div className="label-eyebrow mb-2">Assignees ({(data.assignees || []).length})</div>
              {(data.assignees || []).length === 0 ? (
                <div className="text-xs text-foreground/60 border border-dashed border-border p-3" data-testid="role-inspector-no-assignees">No one is currently using this role.</div>
              ) : (
                <div className="border border-border divide-y divide-border" data-testid="role-inspector-assignees">
                  {(data.assignees || []).map((a) => (
                    <div key={a.id} className="px-3 py-2 text-xs flex items-center justify-between" data-testid={`role-inspector-user-${a.id}`}>
                      <div><strong>{a.name || a.email}</strong> <span className="text-foreground/50">· {a.email}</span></div>
                      <span className="text-foreground/50">{a.last_seen_at ? `last seen ${new Date(a.last_seen_at).toLocaleDateString()}` : "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-2 gap-3">
                <div className="label-eyebrow">What this role can do <span className="text-[11px] text-foreground/50 ml-2 normal-case tracking-normal">{totalGranted} permission{totalGranted === 1 ? "" : "s"}</span></div>
                <div className="relative max-w-xs flex-1">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
                  <Input
                    className="pl-8 h-8 text-xs"
                    placeholder="Search permissions…"
                    value={permQ}
                    onChange={(e) => setPermQ(e.target.value)}
                    data-testid="role-inspector-perm-search"
                  />
                </div>
              </div>
              <div className="space-y-3">
                {groups.map((g) => {
                  const granted = g.permissions.filter((p) => (data.permissions || []).includes(p.code));
                  const visible = q ? granted.filter((p) => p.label.toLowerCase().includes(q) || p.code.toLowerCase().includes(q)) : granted;
                  if (granted.length === 0) return null;
                  if (q && visible.length === 0) return null;
                  return (
                    <div key={g.label} className="border border-border p-3" data-testid={`role-inspector-group-${g.label}`}>
                      <div className="font-semibold text-sm mb-1">{g.label} <span className="text-[11px] text-foreground/50 ml-2">{visible.length}/{g.permissions.length}</span></div>
                      <ul className="text-xs space-y-0.5">
                        {visible.map((p) => (
                          <li key={p.code} className="flex items-center gap-2" data-testid={`role-inspector-perm-${p.code}`}>
                            <Check className="w-3 h-3 text-primary shrink-0" />
                            <span>{p.label} <code className="text-[10px] text-foreground/50 font-mono ml-1">{p.code}</code></span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
                {(data.permissions || []).length === 0 && (
                  <div className="text-xs text-foreground/60 border border-dashed border-border p-3">This role has no permissions yet.</div>
                )}
                {q && totalGranted > 0 && groups.every((g) => {
                  const granted = g.permissions.filter((p) => (data.permissions || []).includes(p.code));
                  const visible = granted.filter((p) => p.label.toLowerCase().includes(q) || p.code.toLowerCase().includes(q));
                  return visible.length === 0;
                }) && (
                  <div className="text-xs text-foreground/60 border border-dashed border-border p-3" data-testid="role-inspector-no-match">No permission matches “{permQ}”.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Iter 236c · Live "what this role sees" preview panel ─────────────────
// Mirrors the tab visibility + finance-column logic used by BrandDetail so
// admins can eyeball the impact of their perm changes without leaving the
// editor. Deliberately dumb — reads only the current `perms` Set, no API
// round-trip needed.
const _BRAND_TABS_META = [
  { key: "overview",         label: "Overview",              view: "brand.tab.overview.view",         edit: "brand.tab.overview.edit" },
  { key: "strategy",         label: "Strategy & SWOT",        view: "brand.tab.strategy.view",         edit: "brand.tab.strategy.edit" },
  { key: "competitors",      label: "Competitors",            view: "brand.tab.competitors.view",      edit: "brand.tab.competitors.edit" },
  { key: "identity",         label: "Voice & Visual",         view: "brand.tab.identity.view",         edit: "brand.tab.identity.edit" },
  { key: "digital",          label: "Digital",                view: "brand.tab.digital.view",          edit: "brand.tab.digital.edit" },
  { key: "pillars",          label: "Pillars, Dates & Ops",   view: "brand.tab.pillars.view",          edit: "brand.tab.pillars.edit" },
  { key: "packages",         label: "Packages",               view: "brand.tab.packages.view",         edit: "brand.tab.packages.edit" },
  { key: "content-strategy", label: "Content Strategy",       view: "brand.tab.content_strategy.view", edit: "brand.tab.content_strategy.edit" },
  { key: "content-bucket",   label: "Content Bucket",         view: "brand.tab.content_bucket.view",   edit: "brand.tab.content_bucket.edit" },
  { key: "gtm",              label: "GTM",                    view: "brand.tab.gtm.view",              edit: "brand.tab.gtm.edit" },
  { key: "projects",         label: "Projects",               view: "brand.tab.projects.view",         edit: "brand.tab.projects.edit" },
  { key: "log",              label: "Log",                    view: "brand.tab.log.view",              edit: "brand.tab.log.edit" },
  { key: "seo",              label: "SEO",                    view: "brand.tab.seo.view",              edit: "brand.tab.seo.edit" },
  { key: "emails",           label: "Emails",                 view: "brand.tab.emails.view",           edit: "brand.tab.emails.edit" },
  { key: "activity",         label: "Activity",               view: "brand.tab.activity.view" },
];
const _FINANCE_ROWS = [
  { code: "brand.finance.view_rates",         label: "Sale rates on packages/estimates" },
  { code: "brand.finance.view_costs",         label: "Internal costs on packages" },
  { code: "brand.finance.view_profitability", label: "Margin %, gross profit, profitability" },
  { code: "brand.finance.view_billing",       label: "Billed vs unbilled tasks & AR summary" },
  { code: "brand.finance.manage",             label: "Edit rates / costs / financial fields" },
];
const _PROJECT_FIN = [
  { code: "project.finance.view_rates",         label: "Project sale/billable rates" },
  { code: "project.finance.view_costs",         label: "Project resource costs" },
  { code: "project.finance.view_profitability", label: "Project margin %, burn vs budget" },
  { code: "project.finance.view_billing",       label: "Billed vs unbilled tasks (project scope)" },
];

function RolePreviewPanel({ perms, onJumpTo }) {
  const has = (code) => perms.has(code);
  const isAdminOnBrand = has("brand.manage") || has("workspace.manage");
  const visibleTabs = _BRAND_TABS_META.filter((t) => isAdminOnBrand || !t.view || has(t.view));
  const editableTabs = _BRAND_TABS_META.filter((t) => t.edit && (isAdminOnBrand || has(t.edit)));
  const hiddenTabs = _BRAND_TABS_META.filter((t) => t.view && !isAdminOnBrand && !has(t.view));

  const canCreateBrand = has("brand.manage");
  const canViewBrandMenu = has("brand.view") || has("brand.manage");

  const jump = (code) => onJumpTo?.(code);

  return (
    <div className="border border-primary/30 bg-primary/5 p-4 space-y-4" data-testid="role-preview-panel">
      <div className="flex items-center gap-2">
        <Shield className="w-4 h-4 text-primary" />
        <h4 className="text-sm font-semibold uppercase tracking-widest">What this role sees</h4>
        <span className="text-[10px] text-muted-foreground ml-auto">Preview only — save the role to apply</span>
      </div>

      {/* Brand access summary */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Brand module</div>
        {canViewBrandMenu ? (
          <div className="text-xs mb-2">
            <strong>Brand menu:</strong> visible ·{" "}
            <strong>Create/edit brands:</strong> {canCreateBrand ? <span className="text-emerald-700">yes</span> : <span className="text-muted-foreground">no</span>}
          </div>
        ) : (
          <div className="text-xs text-amber-700 mb-2">This role can&apos;t see the Brand module at all (no <code>brand.view</code>).</div>
        )}
        {canViewBrandMenu && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1 text-xs" data-testid="role-preview-tabs">
            {_BRAND_TABS_META.map((t) => {
              const canView = isAdminOnBrand || !t.view || has(t.view);
              const canEdit = t.edit && (isAdminOnBrand || has(t.edit));
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => jump(t.view || t.edit)}
                  className="flex items-center gap-1.5 text-left hover:bg-primary/10 -mx-1 px-1 rounded transition"
                  data-testid={`role-preview-tab-${t.key}`}
                  title={`Jump to '${t.view || t.edit}' permission below`}
                >
                  {canView ? <Check className="w-3 h-3 text-emerald-600 shrink-0" /> : <X className="w-3 h-3 text-rose-500 shrink-0" />}
                  <span className={canView ? "" : "text-muted-foreground line-through"}>{t.label}</span>
                  {canView && t.edit && (
                    <span className={`ml-auto text-[9px] font-mono ${canEdit ? "text-emerald-700" : "text-amber-700"}`}>
                      {canEdit ? "R+W" : "R"}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {canViewBrandMenu && (
          <div className="text-[10px] text-muted-foreground mt-2">
            {visibleTabs.length}/{_BRAND_TABS_META.length} tabs visible · {editableTabs.length} editable
            {hiddenTabs.length > 0 && <> · hidden: {hiddenTabs.map((t) => t.label).join(", ")}</>}
          </div>
        )}
      </div>

      {/* Financial visibility */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Financial visibility (brand)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1 text-xs">
          {_FINANCE_ROWS.map((r) => {
            const ok = isAdminOnBrand || has(r.code);
            return (
              <button
                key={r.code}
                type="button"
                onClick={() => jump(r.code)}
                className="flex items-center gap-1.5 text-left hover:bg-primary/10 -mx-1 px-1 rounded transition"
                data-testid={`role-preview-fin-${r.code}`}
                title={`Jump to '${r.code}' below`}
              >
                {ok ? <Check className="w-3 h-3 text-emerald-600 shrink-0" /> : <X className="w-3 h-3 text-rose-500 shrink-0" />}
                <span className={ok ? "" : "text-muted-foreground line-through"}>{r.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Project finance */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Financial visibility (projects)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1 text-xs">
          {_PROJECT_FIN.map((r) => {
            const ok = has("project.finance.manage") || has(r.code);
            return (
              <button
                key={r.code}
                type="button"
                onClick={() => jump(r.code)}
                className="flex items-center gap-1.5 text-left hover:bg-primary/10 -mx-1 px-1 rounded transition"
                data-testid={`role-preview-pfin-${r.code}`}
                title={`Jump to '${r.code}' below`}
              >
                {ok ? <Check className="w-3 h-3 text-emerald-600 shrink-0" /> : <X className="w-3 h-3 text-rose-500 shrink-0" />}
                <span className={ok ? "" : "text-muted-foreground line-through"}>{r.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}



// ─── Iter 236e · Role audit drawer ──────────────────────────────────────
function RoleAuditDrawer({ wsId, role, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/workspaces/${wsId}/roles/audit-log`, { params: { role_slug: role.slug, limit: 200 } })
      .then((r) => { if (alive) setRows(r.data.rows || []); })
      .catch((e) => { if (alive) { toast.error(apiErrorMessage(e, "Couldn't load audit log")); setRows([]); } });
    return () => { alive = false; };
  }, [wsId, role.slug]);

  const fmtWhen = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso || ""; }
  };
  const actionBadgeCls = {
    created: "bg-emerald-500/10 text-emerald-700 border-emerald-500/40",
    updated: "bg-blue-500/10 text-blue-700 border-blue-500/40",
    deleted: "bg-rose-500/10 text-rose-700 border-rose-500/40",
    preset_synced: "bg-violet-500/10 text-violet-700 border-violet-500/40",
    imported: "bg-amber-500/10 text-amber-700 border-amber-500/40",
    overwritten: "bg-amber-500/10 text-amber-700 border-amber-500/40",
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="role-audit-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4" /> Change history · {role.name}
          </DialogTitle>
        </DialogHeader>
        {rows === null && <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>}
        {rows !== null && rows.length === 0 && (
          <div className="text-sm text-muted-foreground py-8 text-center border-2 border-dashed border-border" data-testid="role-audit-empty">
            No changes recorded yet for this role.
            <div className="text-[10px] mt-2">
              Audit logging started in Iter 236e — older role edits aren&apos;t retroactively captured.
            </div>
          </div>
        )}
        {rows !== null && rows.length > 0 && (
          <div className="space-y-3" data-testid="role-audit-list">
            {rows.map((r) => (
              <div key={r.id} className="border border-border p-3 text-xs" data-testid={`role-audit-row-${r.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] font-semibold uppercase tracking-wide ${actionBadgeCls[r.action] || "bg-muted"}`}>
                    {r.action}
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground">{fmtWhen(r.created_at)}</span>
                  <span className="ml-auto text-[10px]">
                    by <span className="font-semibold">{r.actor_name || r.actor_email || r.actor_id}</span>
                  </span>
                </div>
                {r.reason && (
                  <div className="text-[11px] italic text-muted-foreground mb-2 pl-2 border-l-2 border-primary/40">
                    &ldquo;{r.reason}&rdquo;
                  </div>
                )}
                <AuditDiff diff={r.diff || {}} />
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AuditDiff({ diff }) {
  const perms = diff.permissions;
  const scalarKeys = Object.keys(diff).filter((k) => k !== "permissions" && k !== "nav_overrides");
  return (
    <div className="space-y-1">
      {perms && (perms.added?.length > 0 || perms.removed?.length > 0) && (
        <div>
          {perms.added?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-1" data-testid="audit-diff-added">
              <span className="text-[10px] text-muted-foreground mr-1">+ granted:</span>
              {perms.added.map((p) => (
                <span key={p} className="font-mono text-[10px] px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/40 text-emerald-800">{p}</span>
              ))}
            </div>
          )}
          {perms.removed?.length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid="audit-diff-removed">
              <span className="text-[10px] text-muted-foreground mr-1">− revoked:</span>
              {perms.removed.map((p) => (
                <span key={p} className="font-mono text-[10px] px-1.5 py-0.5 bg-rose-500/10 border border-rose-500/40 text-rose-800">{p}</span>
              ))}
            </div>
          )}
        </div>
      )}
      {scalarKeys.map((k) => (
        <div key={k} className="text-[11px]">
          <span className="text-muted-foreground">{k}:</span>{" "}
          <span className="line-through text-muted-foreground">{String(diff[k]?.from ?? "")}</span>{" → "}
          <span className="font-semibold">{String(diff[k]?.to ?? "")}</span>
        </div>
      ))}
      {diff.nav_overrides && (
        <details className="text-[10px] text-muted-foreground">
          <summary className="cursor-pointer">Nav overrides diff</summary>
          <pre className="mt-1 p-2 bg-muted overflow-x-auto">{JSON.stringify(diff.nav_overrides, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}


// R158x · Feb 2026 — Deactivate role with mandatory reassign step.
// Fetches the list of holders + a target-role picker before flipping
// the role inactive.
function DeactivateRoleDialog({ wsId, role, candidates, onCancel, onDone }) {
  const [assignees, setAssignees] = useState(null);
  const [toSlug, setToSlug] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/workspaces/${wsId}/roles/${role.id}/inspect`).then((r) => {
      if (alive) setAssignees(r.data.assignees || []);
    }).catch(() => { if (alive) setAssignees([]); });
    return () => { alive = false; };
  }, [wsId, role.id]);

  const submit = async () => {
    // R158y · Feb 2026 — Skip reassign when there are no holders; go
    // straight to deactivate (consistent single-flow dialog for both
    // empty and populated roles).
    setBusy(true);
    try {
      if (assignees && assignees.length > 0) {
        if (!toSlug) { setBusy(false); return; }
        const r = await api.post(`/workspaces/${wsId}/roles/${role.id}/reassign`, { to_slug: toSlug });
        toast.success(`Reassigned ${r.data?.reassigned || 0} user(s) to ${toSlug}`);
      }
      await api.post(`/workspaces/${wsId}/roles/${role.id}/deactivate`);
      toast.success(`Role "${role.name}" deactivated`);
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Deactivate failed"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="role-deactivate-dialog">
        <DialogHeader>
          <DialogTitle>Deactivate role &quot;{role.name}&quot;</DialogTitle>
          <DialogDescription className="sr-only">
            Confirm deactivating this role. If it has active holders you&apos;ll be asked to pick a replacement role first.
          </DialogDescription>
        </DialogHeader>
        {(role.assignee_count || 0) > 0 && (
          <div className="text-xs text-muted-foreground -mt-2">
            Before this role can be turned off, its {role.assignee_count || 0} active user(s) need a new role.
            Pick a replacement — everyone on <b>{role.name}</b> will be moved to it in one click.
          </div>
        )}
        {assignees === null ? (
          <div className="text-xs italic p-4 text-center text-muted-foreground">Loading holders…</div>
        ) : assignees.length === 0 ? (
          <div className="border border-dashed border-border p-3 text-xs text-muted-foreground">
            No active holders. Confirm to deactivate directly.
          </div>
        ) : (
          <div className="border border-border max-h-40 overflow-y-auto" data-testid="role-deactivate-holders">
            {assignees.map((a) => (
              <div key={a.id} className="px-3 py-1.5 text-xs border-b border-border last:border-b-0" data-testid={`role-deactivate-holder-${a.id}`}>
                <span className="font-medium">{a.name || a.email}</span>
                {a.email && a.name ? <span className="text-muted-foreground ml-1">({a.email})</span> : null}
              </div>
            ))}
          </div>
        )}
        {(assignees && assignees.length > 0) && (
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Reassign holders to</div>
            <select
              className="w-full h-9 border border-input px-2 text-sm rounded-none bg-background"
              value={toSlug}
              onChange={(e) => setToSlug(e.target.value)}
              data-testid="role-deactivate-target"
            >
              <option value="">Pick a target role…</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.slug}>{c.name} ({c.slug})</option>
              ))}
            </select>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-none" onClick={onCancel} disabled={busy} data-testid="role-deactivate-cancel">Cancel</Button>
          <Button
            className="rounded-none"
            onClick={submit}
            disabled={busy || (assignees && assignees.length > 0 && !toSlug)}
            data-testid="role-deactivate-confirm"
          >
            {busy ? "Working…" : (assignees && assignees.length > 0 ? "Reassign & deactivate" : "Deactivate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// R164a · Feb 2026 — Cleanup orphan role assignments.
// Renders a preview of every stale `.role` value on workspace_members
// (slug no longer in workspace_roles) grouped by slug + user count, and
// lets the admin bulk-reassign them all to a picked target role.
function CleanupOrphansDialog({ wsId, candidates, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${wsId}/roles/orphans`);
        setPreview(data);
      } catch (e) { toast.error(apiErrorMessage(e, "Couldn't scan orphans")); onClose(); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [wsId]);

  const submit = async () => {
    if (!target) { toast.error("Pick a target role"); return; }
    if (!window.confirm(
      `Reassign ${preview?.total_orphan_users || 0} member(s) across ${preview?.orphan_slugs?.length || 0} orphan slug(s) to "${candidates.find((c) => c.slug === target)?.name}"? This cannot be undone.`))
      return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${wsId}/roles/orphans/reassign`,
        { target_slug: target });
      toast.success(`Reassigned ${data.reassigned} member(s) across ${Object.keys(data.per_slug || {}).length} slug(s) to ${data.target}`);
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Reassign failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="roles-cleanup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-primary" /> Cleanup orphan role assignments
          </DialogTitle>
          <DialogDescription>
            Members with a role slug that no longer exists in this workspace show up as
            <code className="mx-1 text-red-600">(missing)</code>
            on the User Management page. Bulk-reassign them all to a valid role in one click.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-sm text-muted-foreground">Scanning workspace…</div>
        ) : (preview?.total_orphan_users || 0) === 0 ? (
          <div className="py-6 text-sm text-emerald-700 border border-emerald-500/40 bg-emerald-500/5 p-3"
                data-testid="roles-cleanup-empty">
            No orphan assignments — every member's role is present in the workspace. Nothing to fix 🎉
          </div>
        ) : (
          <>
            <div className="border border-border max-h-56 overflow-y-auto"
                  data-testid="roles-cleanup-preview">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest bg-muted/20 text-muted-foreground">
                    <th className="text-left px-3 py-1.5">Orphan slug</th>
                    <th className="text-right px-3 py-1.5 w-20">Users</th>
                    <th className="text-left px-3 py-1.5">Sample</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview?.orphan_slugs || []).map((r) => (
                    <tr key={r.orphan_slug} className="border-t border-border">
                      <td className="px-3 py-1.5 font-mono text-red-600">{r.orphan_slug}</td>
                      <td className="px-3 py-1.5 text-right font-semibold">{r.user_count}</td>
                      <td className="px-3 py-1.5 truncate max-w-[250px] text-muted-foreground">
                        {r.users.map((u) => u.name || u.email || u.id).slice(0, 3).join(", ")}
                        {r.users.length > 3 && ` +${r.user_count - 3} more`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-xs">
              <div className="label-eyebrow mb-1">Reassign every orphan to:</div>
              <select value={target} onChange={(e) => setTarget(e.target.value)}
                       className="w-full h-9 border border-border rounded-none text-sm bg-background px-2"
                       data-testid="roles-cleanup-target">
                <option value="">— choose a valid role —</option>
                {candidates.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name} ({c.slug})</option>
                ))}
              </select>
            </div>
            <div className="text-[11px] text-muted-foreground border-l-2 border-amber-500 pl-2 py-1 bg-amber-500/5">
              Workspace owners are never demoted — even if their slug is orphaned, they'll keep owner rights unless you transfer ownership first.
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Close</Button>
          {(preview?.total_orphan_users || 0) > 0 && (
            <Button onClick={submit} disabled={!target || busy}
                     className="rounded-none"
                     data-testid="roles-cleanup-submit">
              {busy ? "Reassigning…" : `Reassign ${preview?.total_orphan_users || 0} member(s)`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Feb 2026 · One-click "Add from template" picker (Studio Manager · Studio Viewer · …).
function RoleTemplatePicker({ wsId, onClose, onDone }) {
  const [tpls, setTpls] = useState([]);
  const [busySlug, setBusySlug] = useState(null);
  useEffect(() => {
    api.get(`/role-templates`).then((r) => setTpls(r.data?.templates || []))
      .catch((e) => toast.error(apiErrorMessage(e, "Load failed")));
  }, []);
  const create = async (tpl) => {
    setBusySlug(tpl.slug);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/roles/from-template`, { template: tpl.slug });
      toast.success(`Added role "${data.name}"`);
      onDone && onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusySlug(null); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="roles-template-picker">
        <DialogHeader>
          <DialogTitle>Add role from template</DialogTitle>
          <DialogDescription className="sr-only">
            Pick a preset role and it will be created in this workspace ready for assignment.
          </DialogDescription>
        </DialogHeader>
        {tpls.length === 0 ? (
          <div className="text-xs italic p-4 text-center text-muted-foreground">Loading templates…</div>
        ) : (
          <div className="space-y-2" data-testid="roles-template-list">
            {tpls.map((t) => (
              <div key={t.slug} className="border border-border p-3 flex items-start gap-3" data-testid={`roles-template-${t.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{t.description}</div>
                  <div className="text-[10px] text-primary mt-1 uppercase tracking-widest">{t.permission_count} permissions</div>
                </div>
                <Button size="sm" className="rounded-none h-8"
                        onClick={() => create(t)} disabled={busySlug === t.slug}
                        data-testid={`roles-template-add-${t.slug}`}>
                  {busySlug === t.slug ? "Adding…" : "Add"}
                </Button>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Feb 2026 · Super-admin orphan-role sweep — scan every workspace for
// active members holding a role slug that isn't in workspace_roles.
function OrphanSweepDialog({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/roles/orphan-sweep`);
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Sweep failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const fix = async (row, orphan) => {
    const target = window.prompt(`Reassign "${orphan.slug}" holders (${orphan.holder_count} users) to which role slug?\n\nUse a real slug in "${row.workspace_name}" — e.g. team_member`);
    if (!target) return;
    setBusy(`${row.workspace_id}:${orphan.slug}`);
    setMsg(null);
    try {
      const dry = await api.post(`/admin/roles/orphan-fix`, {
        workspace_id: row.workspace_id, from_slug: orphan.slug, to_slug: target, apply: false });
      if (!window.confirm(`Dry-run: this will reassign ${dry.data.would_update} user(s) from "${orphan.slug}" to "${target}" in "${row.workspace_name}". Apply now?`)) {
        setBusy(null); return;
      }
      const { data: res } = await api.post(`/admin/roles/orphan-fix`, {
        workspace_id: row.workspace_id, from_slug: orphan.slug, to_slug: target, apply: true });
      setMsg(`✓ Reassigned ${res.updated} user(s) in ${row.workspace_name}.`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Fix failed")); }
    finally { setBusy(null); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl rounded-none" data-testid="roles-orphan-sweep-dialog">
        <DialogHeader>
          <DialogTitle>Orphan role sweep — all workspaces</DialogTitle>
          <DialogDescription className="sr-only">
            Super-admin dashboard listing every role slug held by active workspace members
            that does not have a matching workspace_roles row.
          </DialogDescription>
        </DialogHeader>
        {msg && <div className="text-xs border border-emerald-500 bg-emerald-50 text-emerald-800 p-2" data-testid="orphan-sweep-msg">{msg}</div>}
        {loading ? (
          <div className="text-xs italic text-muted-foreground p-4 text-center">Scanning workspaces…</div>
        ) : !data || data.workspace_count === 0 ? (
          <div className="text-sm text-center p-6 border border-emerald-500 bg-emerald-50/40">
            🎉 No orphan roles found across any workspace.
          </div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground mb-2" data-testid="orphan-sweep-total">
              {data.total_orphan_users} user(s) hold {data.workspaces.reduce((s, w) => s + w.orphans.length, 0)} orphan slug(s) across {data.workspace_count} workspace(s).
            </div>
            <div className="max-h-[60vh] overflow-y-auto border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px]">Workspace</th>
                    <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px]">Orphan slug</th>
                    <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px]">Holders</th>
                    <th className="text-left px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px]">Sample users</th>
                    <th className="text-right px-2 py-1.5 font-semibold uppercase tracking-widest text-[10px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.workspaces.map((w) => w.orphans.map((o) => (
                    <tr key={`${w.workspace_id}:${o.slug}`} className="border-t border-border" data-testid={`orphan-row-${o.slug}`}>
                      <td className="px-2 py-1.5">{w.workspace_name}<div className="text-[10px] text-muted-foreground font-mono">{w.workspace_id.slice(0, 8)}</div></td>
                      <td className="px-2 py-1.5 font-mono text-rose-700">{o.slug}</td>
                      <td className="px-2 py-1.5 font-mono">{o.holder_count}</td>
                      <td className="px-2 py-1.5">
                        {(o.sample_users || []).slice(0, 3).map((u) => u?.email || u?.name || u?.id).join(", ")}
                        {(o.holder_count > 3) && ` +${o.holder_count - 3}`}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Button size="sm" className="rounded-none h-7 text-xs"
                                disabled={busy === `${w.workspace_id}:${o.slug}`}
                                onClick={() => fix(w, o)}
                                data-testid={`orphan-fix-${w.workspace_id}-${o.slug}`}>
                          Reassign…
                        </Button>
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={load} disabled={loading}>Refresh</Button>
          <Button className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

